import { useEffect, useId, useState } from 'react';
import { Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { fileReaderToBase64 } from '../../../helpers';
import { fileValidator } from '../../../utils/dragAndDrop';
import DragAndDropContainer from './DragAndDropContainer';
import styles from './UploadFileModal.module.scss';

const DEFAULT_ACCEPT = `application/pdf, text/xml, .doc, .docx, application/msword,
	application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/*`;

const UploadFileModal = ({
  setUploadFiles,
  isMultiple = true,
  accept = DEFAULT_ACCEPT,
  title,
  hint,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const inputId = useId();
  const ctaLabel = title || t('common.upload.cta');
  const hintLabel = hint || t('common.upload.hint');

  useEffect(() => {
    if (!error) return undefined;
    const timer = window.setTimeout(() => setError(false), 3000);
    return () => window.clearTimeout(timer);
  }, [error]);

  const applyFiles = async (fileList) => {
    const file = Object.values(fileList || {});
    if (!file?.length) return;

    const { errVal } = fileValidator(file, { fileSizeMBLimit: 60 });
    if (errVal) {
      setError(errVal);
      return;
    }

    const base64FileUrl = await fileReaderToBase64(file);
    if (isMultiple) {
      setUploadFiles((prevState) => [
        ...prevState,
        {
          name: file[0].name,
          url: base64FileUrl,
        },
      ]);
    } else {
      setUploadFiles({
        name: file[0].name,
        url: base64FileUrl,
      });
    }
  };

  return (
    <div className={styles['upload-file__wrapper']}>
      <DragAndDropContainer
        setUploadFiles={setUploadFiles}
        setError={setError}
        isMultiple={isMultiple}
      >
        <div className={styles['upload-file__btn']}>
          <label htmlFor={inputId} className={styles['btn__label']}>
            <span className={styles.icon_wrap} aria-hidden="true">
              <Upload size={22} strokeWidth={2.2} />
            </span>
            <span className={styles.cta}>{ctaLabel}</span>
            <span className={styles.hint}>{hintLabel}</span>
            {error ? <p className={styles.error}>{error}</p> : null}
          </label>
          <input
            className={styles['upload-file__input']}
            onChange={async (e) => {
              await applyFiles(e.target.files);
              e.target.value = '';
            }}
            name="file"
            id={inputId}
            type="file"
            accept={accept}
          />
        </div>
      </DragAndDropContainer>
    </div>
  );
};

export default UploadFileModal;
